import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiCopy, FiCheck, FiAlertTriangle, FiImage, FiX } from 'react-icons/fi';
import { useI18n } from '../../hooks/useI18n';

export function DepositModal({ open, onClose, walletAddress, walletName, walletType, pushToast }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [imageCopied, setImageCopied] = useState(false);
  const qrRef = useRef(null);

  if (!open || !walletAddress) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    pushToast?.(t('wallet.addressCopied'), 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyImage = async () => {
    if (!qrRef.current) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(qrRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });

      canvas.toBlob(async (blob) => {
        if (!blob) {
          pushToast?.(t('wallet.imageGenFailed'), 'error');
          return;
        }
        try {
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          setImageCopied(true);
          pushToast?.(t('wallet.qrCopied'), 'success');
          setTimeout(() => setImageCopied(false), 2000);
        } catch (err) {
          console.error('Clipboard write failed:', err);
          pushToast?.(t('wallet.imageCopyFailed'), 'error');
        }
      }, 'image/png');
    } catch (err) {
      console.error('html2canvas failed:', err);
      pushToast?.(t('wallet.imageGenError'), 'error');
    }
  };

  const isSafe = walletType?.toLowerCase() === 'safe';

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('wallet.deposit')}</h3>
            <p className="text-sm text-gray-400">{walletName || 'Wallet'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center gap-4">
          {/* Network Warning */}
          <div className="w-full bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-3">
            <FiAlertTriangle className="text-amber-500 text-xl shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200/90 leading-relaxed">
              <p className="font-bold text-amber-500 mb-1">{t('wallet.depositWarningTitle')}</p>
              {t('wallet.depositWarningLine1')}
              <br />
              {t('wallet.depositWarningLine2')}
            </div>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-3">
            <div ref={qrRef} className="p-3 bg-white rounded-2xl shadow-lg">
              <QRCodeSVG
                value={walletAddress}
                size={160}
                level="M"
                includeMargin={true}
                fgColor="#000000"
                bgColor="#FFFFFF"
              />
            </div>

            <button
              onClick={handleCopyImage}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                imageCopied
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'bg-surface-highlight text-gray-400 border border-glass-border hover:text-white'
              }`}
            >
              {imageCopied ? <FiCheck size={14} /> : <FiImage size={14} />}
              <span>{imageCopied ? t('wallet.copiedImage') : t('wallet.saveQr')}</span>
            </button>
          </div>

          {/* Address */}
          <div className="w-full">
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1.5 text-center">
              {t('wallet.depositAddress')}
            </p>
            <div
              onClick={handleCopy}
              className="group flex items-center gap-3 p-3 bg-surface border border-glass-border rounded-xl hover:border-accent/30 transition-all cursor-pointer"
            >
              <code className="flex-1 truncate text-sm font-mono text-gray-300 text-center group-hover:text-white transition-colors">
                {walletAddress}
              </code>
              <div
                className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                  copied ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-500 group-hover:text-accent'
                }`}
              >
                {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
              </div>
            </div>
          </div>

          {/* Token Info */}
          <div className={`grid ${isSafe ? 'grid-cols-1' : 'grid-cols-2'} gap-4 w-full text-center`}>
            <div className="p-3 rounded-xl bg-surface border border-glass-border">
              <p className="text-xs text-gray-500 mb-1">{t('wallet.tradingFunds')}</p>
              <p className="font-bold text-white">USDC.e / USDC</p>
            </div>
            {!isSafe && (
              <div className="p-3 rounded-xl bg-surface border border-glass-border">
                <p className="text-xs text-gray-500 mb-1">{t('wallet.gasFee')}</p>
                <p className="font-bold text-white">POL (Matic)</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
